import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, RefreshCw, CheckCircle2, XCircle, Eye, X } from "lucide-react";

export default function SyncLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/sync-logs");
        if (alive) setLogs(data.logs || []);
      } catch (e) {
        // error handling
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[#0F172A]">Sync Logs</h1>
          <p className="text-sm text-[#64748B] mt-1">
            Riwayat sinkronisasi data dari Accurate ke Midware.
          </p>
        </div>
        <button
          onClick={() => setReloadKey((k) => k + 1)}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] hover:border-[#0066FF] hover:text-[#0066FF] text-[#0F172A] rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="border border-[#E2E8F0] rounded-lg bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FA]">
            <tr className="text-xs font-medium text-[#64748B] uppercase tracking-wider">
              <th className="px-6 py-3 text-left">Waktu Selesai</th>
              <th className="px-6 py-3 text-left">Status</th>
              <th className="px-6 py-3 text-center">Data Ditarik</th>
              <th className="px-6 py-3 text-center">Invoice Ditambah</th>
              <th className="px-6 py-3 text-center">Invoice Diubah</th>
              <th className="px-6 py-3 text-center">Invoice Dihapus</th>
              <th className="px-6 py-3 text-left">Keterangan Error</th>
              <th className="px-6 py-3 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Memuat...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-sm text-[#64748B]">
                  Belum ada riwayat sinkronisasi.
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr key={log._id} className="hover:bg-[#F8F9FA] transition-colors">
                  <td className="px-6 py-3 whitespace-nowrap text-[#0F172A]">
                    {new Date(log.completed_at).toLocaleString("id-ID")}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-1.5">
                      {log.status === "SUCCESS" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#D1FAE5] text-[#059669]">
                          <CheckCircle2 className="w-3 h-3" /> Sukses
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-[#FEE2E2] text-[#DC2626]">
                          <XCircle className="w-3 h-3" /> Gagal
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-center text-[#475569]">
                    {log.invoices_synced} invoice
                  </td>
                  <td className="px-6 py-3 text-center text-[#475569]">
                    {log.invoices_added > 0 ? (
                      <span className="text-green-600 font-medium">+{log.invoices_added}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-6 py-3 text-center text-[#475569]">
                    {log.invoices_updated > 0 ? (
                      <span className="text-blue-600 font-medium">{log.invoices_updated}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-6 py-3 text-center text-[#475569]">
                    {log.invoices_deleted > 0 ? (
                      <span className="text-red-600 font-medium">-{log.invoices_deleted}</span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-6 py-3 text-[#475569] text-xs max-w-xs truncate" title={log.error || "-"}>
                    {log.error || "-"}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <button
                      onClick={() => setSelectedLog(log)}
                      disabled={!log.details}
                      className="text-[#0066FF] hover:text-[#0052CC] disabled:opacity-30 p-1 rounded transition-colors"
                      title={log.details ? "Lihat Detail" : "Tidak ada detail"}
                    >
                      <Eye className="w-4 h-4 mx-auto" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Detail */}
      {selectedLog && selectedLog.details && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
              <div>
                <h3 className="font-semibold text-lg text-[#0F172A]">Detail Sinkronisasi</h3>
                <p className="text-sm text-[#64748B]">
                  {new Date(selectedLog.completed_at).toLocaleString("id-ID")}
                </p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-[#64748B] hover:text-[#0F172A] p-2 rounded-md transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1 space-y-6">
              <div>
                <h4 className="font-medium text-sm text-[#059669] flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4" /> 
                  Ditambah ({selectedLog.details.added_invoices?.length || 0})
                </h4>
                <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 text-xs text-[#334155] min-h-[40px] max-h-32 overflow-y-auto">
                  {selectedLog.details.added_invoices?.length > 0 
                    ? selectedLog.details.added_invoices.join(", ") 
                    : "Tidak ada faktur yang ditambah."}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm text-[#0066FF] flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4" /> 
                  Diubah ({selectedLog.details.updated_invoices?.length || 0})
                </h4>
                <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 text-xs text-[#334155] min-h-[40px] max-h-48 overflow-y-auto">
                  {selectedLog.details.updated_invoices?.length > 0 
                    ? selectedLog.details.updated_invoices.join(", ") 
                    : "Tidak ada faktur yang diubah."}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-sm text-[#DC2626] flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4" /> 
                  Dihapus ({selectedLog.details.deleted_invoices?.length || 0})
                </h4>
                <div className="bg-[#FEE2E2]/30 border border-[#FECACA] rounded-md p-3 text-xs text-[#991B1B] min-h-[40px] max-h-32 overflow-y-auto">
                  {selectedLog.details.deleted_invoices?.length > 0 
                    ? selectedLog.details.deleted_invoices.join(", ") 
                    : "Tidak ada faktur yang dihapus."}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="bg-white border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F1F5F9] font-medium text-sm px-4 py-2 rounded-md transition-colors"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
